import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Candy, Sparkles, Wine, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PlayerAvatar, PIECE_COLORS } from "./PlayerAvatar";
import { BrandMark } from "./GameVisuals";

const CANDY = { red: "#E11D48", orange: "#F97316", yellow: "#EAB308", green: "#16A34A", blue: "#2563EB", purple: "#9333EA" };
const pmap = (g) => Object.fromEntries((g.players || []).map((p) => [p.player_id, p]));
const turnOf = (st) => st?.order?.[st.turn % Math.max(1, st.order.length)];

export function WhoseTurn({ g, me, verb = "play" }) {
  const st = g.state;
  if (!st) return null;
  const P = pmap(g);
  if (st.phase === "over") return <p className="text-center font-serif text-2xl text-[var(--gold)]" data-testid="game-winner">{st.winner ? `${P[st.winner]?.name || "Someone"} wins!` : "Game over!"}</p>;
  const pid = turnOf(st);
  return <p className="text-center text-sm text-white/70" data-testid="game-turn">{me && pid === me.player_id ? <b className="text-[var(--gold)]">Your turn to {verb}!</b> : <>Waiting on <b className="text-white">{P[pid]?.name || "…"}</b></>}</p>;
}

export function PathRace({ g, me, act, load, big = false }) {
  const st = g.state, sp = g.config.spaces, P = pmap(g);
  const candy = g.type === "candy";
  const [busy, setBusy] = useState(false);
  const color = Object.fromEntries((st?.order || []).map((pid, i) => [pid, PIECE_COLORS[i % PIECE_COLORS.length]]));
  const at = {};
  (st?.order || []).forEach((pid) => { (at[st.pos[pid] || 0] = at[st.pos[pid] || 0] || []).push(pid); });
  const cols = big ? 9 : 6;
  const mine = me && turnOf(st) === me.player_id && st.phase !== "over";
  const lm = st?.last_move;
  async function go() { setBusy(true); await act("play/move", {}, { quiet: true }); setTimeout(() => { setBusy(false); load(); }, 700); }
  return (
    <div className="space-y-4" data-testid="path-race">
      <WhoseTurn g={g} me={me} verb={candy ? "draw a card" : "spin"} />
      <div className="relative rounded-3xl p-3 bg-[radial-gradient(circle_at_center,#1f1a12,#0b0b0b)] border border-[var(--gold)]/30">
        <div className="flex justify-center mb-3"><BrandMark big={big} /></div>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
          {sp.map((s, i) => {
            const row = Math.floor(i / cols), rev = row % 2 === 1;
            const order = rev ? row * cols + (cols - 1 - (i % cols)) : i;
            const treat = candy && g.config.treats?.[String(i)];
            return (
              <div key={i} style={{ order, background: candy ? CANDY[s.color] : s.kind === "pay" ? "#14532d" : s.kind === "spend" ? "#7f1d1d" : "rgba(255,255,255,0.07)" }}
                className={`relative rounded-xl ${big ? "min-h-[86px] text-xs" : "min-h-[64px] text-[9px]"} p-1 flex flex-col items-center justify-center text-center text-white ${i === sp.length - 1 ? "ring-2 ring-[var(--gold)]" : ""}`} data-testid={`path-space-${i}`}>
                {candy ? (treat ? <><Sparkles className="w-3.5 h-3.5" /><span className="leading-tight">{treat}</span></> : i === 0 ? "Start" : i === sp.length - 1 ? "Forever Castle" : <Candy className="w-4 h-4 opacity-60" />)
                  : <><span className="leading-tight">{s.label}</span>{!!s.money && <span className="opacity-80">{s.money > 0 ? "+" : ""}{s.money}</span>}</>}
                <div className="absolute -bottom-1 inset-x-0 flex justify-center gap-0.5">
                  {(at[i] || []).slice(0, 4).map((pid) => <motion.span key={pid} layoutId={`pr-${pid}`}><PlayerAvatar p={P[pid]} size={big ? 26 : 18} color={color[pid]} /></motion.span>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {lm && <p className="text-center text-white/80" data-testid="path-last-move">{P[lm.player_id]?.name}: {candy ? <><span className="inline-block w-3 h-3 rounded-sm align-middle" style={{ background: CANDY[lm.card] }} /> {lm.double ? "double " : ""}{lm.card}</> : <>spun {lm.spin} · {lm.event} ({lm.money >= 0 ? "+" : ""}{lm.money})</>}</p>}
      {me && !big && <div className="flex justify-center"><button disabled={!mine || busy} onClick={go} className="btn-gold disabled:opacity-40" data-testid="path-move-btn">{busy ? "…" : candy ? "Draw a card" : "Spin the wheel"}</button></div>}
      {st?.log?.length > 0 && <ul className={`${big ? "text-base" : "text-xs"} text-white/50 space-y-0.5`} data-testid="path-log">{st.log.slice(0, 4).map((l, i) => <li key={i}>{l}</li>)}</ul>}
    </div>
  );
}

export function ShotGame({ g, me, act, big = false }) {
  const st = g.state, P = pmap(g);
  const giver = turnOf(st);
  const mine = me && giver === me.player_id;
  const [card, setCard] = useState(null);
  const [guess, setGuess] = useState("");
  const shotAt = st?.last_shot?.at ? new Date(st.last_shot.at).getTime() : 0;
  const flash = Date.now() - shotAt < 6000;
  useEffect(() => {
    if (!mine || !me) { setCard(null); return; }
    act("play/card", {}, { quiet: true }).then((d) => d && setCard(d));
  }, [mine, st?.v]); // eslint-disable-line react-hooks/exhaustive-deps
  async function send(e) { e.preventDefault(); const d = await act("play/guess", { text: guess }, { quiet: true }); if (d) { toast[d.correct ? "success" : "message"](d.correct ? "You got it! +20" : "Not it, keep guessing"); if (d.correct) setGuess(""); } }
  return (
    <div className="space-y-4 text-center" data-testid="shot-game">
      {!big && <div className="flex justify-center"><BrandMark /></div>}
      <AnimatePresence>{flash && <motion.p initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }} className={`font-serif text-[var(--gold)] ${big ? "text-8xl" : "text-5xl"}`} data-testid="shot-flash"><Wine className="inline w-[0.8em] h-[0.8em]" /> TAKE A SHOT!</motion.p>}</AnimatePresence>
      <p className={big ? "text-3xl font-serif" : "text-sm text-white/70"} data-testid="shot-giver">{mine ? <b className="text-[var(--gold)]">You're describing! Don't say the off-limits words.</b> : <><b className="text-white">{P[giver]?.name || "…"}</b> is describing a word</>}</p>
      {mine && card && (
        <div className="bg-[#FAF7F2] text-[#111] rounded-3xl p-6 max-w-xs mx-auto" data-testid="shot-card">
          <p className="font-serif text-4xl">{card.card.word}</p>
          <div className="mt-3 space-y-1">{card.card.taboo.map((t) => <p key={t} className="text-rose-600 uppercase text-sm tracking-wider">{t}</p>)}</div>
          <p className="text-xs text-[#666] mt-3">{card.seconds_left}s left</p>
          <button onClick={() => act("play/skip")} className="text-xs underline mt-2" data-testid="shot-pass">Pass</button>
        </div>
      )}
      {me && !mine && !big && (
        <>
          <form onSubmit={send} className="flex gap-2 max-w-sm mx-auto"><input value={guess} onChange={(e) => setGuess(e.target.value)} maxLength={60} placeholder="Your guess" className="flex-1 bg-white/10 rounded-full px-4 py-3 outline-none" data-testid="shot-guess-input" /><button className="btn-gold" data-testid="shot-guess-btn">Guess</button></form>
          <button onClick={() => act("play/buzz")} className="px-6 py-3 rounded-full bg-rose-600 text-white text-sm tracking-wider" data-testid="shot-buzz-btn">TABOO! They said it</button>
        </>
      )}
      <ul className={`${big ? "text-xl" : "text-xs"} text-white/50 space-y-0.5`} data-testid="shot-log">{(st?.log || []).slice(0, 5).map((l, i) => <li key={i}>{l}</li>)}</ul>
    </div>
  );
}

export function MysteryGame({ g, me, act, big = false }) {
  const st = g.state || {}, c = g.config;
  const [vote, setVote] = useState(null);
  const voters = Object.keys(st.votes || {}).length;
  async function cast(i) { const d = await act("play/vote", { suspect: i }, { quiet: true }); if (d) { setVote(i); toast.success(`You accused ${c.suspects[i].name}`); } }
  return (
    <div className="space-y-5" data-testid="mystery-game">
      {!big && <div className="flex justify-center"><BrandMark /></div>}
      <p className={`font-serif ${big ? "text-3xl" : "text-lg"} text-white/85 text-center`} data-testid="mystery-story">{c.story}</p>
      {st.solved && <p className="text-center font-serif text-4xl text-[var(--gold)]" data-testid="mystery-solved">It was {c.suspects[c.culprit]?.name}!</p>}
      <div className={`grid ${big ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
        {c.suspects.map((s, i) => (
          <button key={i} disabled={!me || big || st.solved} onClick={() => cast(i)} data-testid={`mystery-suspect-${i}`}
            className={`rounded-2xl p-3 text-left border ${st.solved && i === c.culprit ? "border-[var(--gold)] bg-[var(--gold)]/20" : vote === i ? "border-[var(--gold)] bg-white/10" : "border-white/10 bg-white/5"}`}>
            <p className={`font-serif ${big ? "text-2xl" : "text-lg"} flex items-center gap-2`}><Search className="w-4 h-4 text-[var(--gold)]" />{s.name}{vote === i && <Check className="w-4 h-4 text-emerald-400" />}</p>
            <p className="text-xs text-white/60">{s.bio}</p>
          </button>
        ))}
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-2">Clues revealed · {c.clues.length} of {c.total_clues}</p>
        <ol className="space-y-1.5" data-testid="mystery-clues">{c.clues.map((cl, i) => <motion.li initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} key={i} className={`${big ? "text-2xl" : "text-sm"} bg-white/5 rounded-xl px-3 py-2`}>{i + 1}. {cl}</motion.li>)}</ol>
      </div>
      <p className="text-xs text-white/50 text-center" data-testid="mystery-votes">{voters} detective{voters === 1 ? "" : "s"} have made an accusation. Guess early for more points!</p>
    </div>
  );
}

export async function hostMystery(pgId, action) { return (await api.post(`/party-games/${pgId}/mystery/${action}`)).data; }
